import { Injectable } from "@angular/core";
import { AppConfig } from "../model/appConfig";
import { ApiService } from "./api.service";

@Injectable()
export class AppService
{
    private static _config: AppConfig | null = null;
    
    public loading: string = "";
    public notification: string = "test";
    public notificationClass: string = "";
    public notificationTimer: any = null;

    private readonly CONFIG_STORAGE_KEY = 'app_config';

    constructor(private apiService: ApiService) {}

    // Getter that automatically loads config if null
    public static get config(): AppConfig | null {
        return AppService._config;
    }

    // Setter for config
    public static set config(value: AppConfig | null) {
        AppService._config = value;
    }

    // Method to safely get config with automatic loading
    public async getConfig(): Promise<AppConfig> {
        if (!AppService.config) {
            await this.loadConfig();
        }
        return AppService.config!;
    }

    public loadConfig(): Promise<void> {
        return new Promise((resolve, reject) => {
            // First try to load from localStorage
            const savedConfig = this.loadConfigFromStorage();
            if (savedConfig) {
                AppService.config = savedConfig;
                resolve();
                return;
            }

            // Fallback to loading from API/assets
            this.apiService.loadAppConfig().subscribe(
                (config: AppConfig) => {
                    AppService.config = config;
                    // Save to localStorage for future use
                    this.saveConfigToStorage(config);
                    resolve();
                },
                (error) => {
                    console.error('Failed to load app config:', error);
                    reject(error);
                }
            );
        });
    }

    public changeConfig(config: AppConfig): void {
        AppService.config = config;
        // Save configuration to localStorage for persistence
        this.saveConfigToStorage(config);
        
        // Show notification that config was saved
        this.showNotification('Configuration saved successfully');
    }

    private saveConfigToStorage(config: AppConfig): void {
        try {
            const configString = JSON.stringify(config, null, 2);
            localStorage.setItem(this.CONFIG_STORAGE_KEY, configString);
        } catch (error) {
            console.error('Failed to save config to localStorage:', error);
        }
    }

    private loadConfigFromStorage(): AppConfig | null {
        try {
            const configString = localStorage.getItem(this.CONFIG_STORAGE_KEY);
            if (configString) {
                return JSON.parse(configString);
            }
        } catch (error) {
            console.error('Failed to load config from localStorage:', error);
        }
        return null;
    }

    public showNotification (s: string): void 
    {
        if (this.notificationTimer)
        {
            clearTimeout (this.notificationTimer);
        }

        this.notification = s;
        this.notificationClass = "active";
        this.notificationTimer = setTimeout (
            () =>
            {
                this.notificationClass = "";
                this.notificationTimer = null;
            }, 2000
        );
    }
}
